import React, { useState } from 'react';
import PROModal from '../PROModal';
import VIPModal from '../VIPModal';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../constants/Url';
import { isDateExpiredUTC } from '../../utils/Utils';

const MemberCard = ({ profile, onRefresh }) => {
  const { t } = useTranslation();
  const [isVIPModalOpen, setIsVIPModalOpen] = useState(false);
  const [isPROModalOpen, setIsPROModalOpen] = useState(false);
  const navigate = useNavigate();

  const { memberType, expiredTS } = profile || {};
  const type = memberType || 'normal'; // normal, trial, pro, VIP

  const handleUpgrade = () => {
    setIsPROModalOpen(true);
  };

  const formattedDate = expiredTS ? new Date(expiredTS).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '.') : '';

  let cardContent = null;

  const isExpiredTrial = type === 'trial' && isDateExpiredUTC(expiredTS);

  if (type === 'trial' && !isExpiredTrial) {
    // Active free trial — show distinct "Free Trial" status
    cardContent = (
      <div className="mx-auto mt-4 w-full" style={{ maxWidth: '300px' }}>
        <div className="bg-[#f8e4ea] border border-[#f8e4ea] rounded-t-lg p-3 flex justify-between items-center relative overflow-hidden gap-2">
          <div className="flex items-center gap-1.5 relative z-10 shrink min-w-0">
            <img
              alt="ESI-Logo"
              role="button"
              src={BASE_URL + "/logo.svg"}
              className="esi-logo h-5 max-w-[70px] object-contain shrink-0"
            />
            <span className="bg-[#e91e63] text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap shrink-0">
              {t("text_free_trial")}
            </span>
          </div>

          <div className="bg-gradient-to-r from-[#F4CC4A] to-[#FFFAC3] p-[1px] rounded-md shrink-0">
            <button
              onClick={handleUpgrade}
              className="bg-gradient-to-r from-[#FFFAC3] to-[#F4CC4A] text-[#7a5a00] text-[11px] font-bold px-2.5 py-1.5 rounded-md cursor-pointer hover:opacity-90 whitespace-nowrap shrink-0 block"
            >
              {t("text_activate_now")} &gt;
            </button>
          </div>
        </div>
        <div className="bg-[#fff0f4] rounded-b-lg px-3 py-2 flex justify-between items-center text-xs font-bold text-[#E50A47]">
          <span className="whitespace-nowrap">{formattedDate} {t("text_valid_till")}</span>
          <span
            onClick={() => navigate('/profiles?tab=purchase-history')}
            className="cursor-pointer hover:opacity-80 shrink-0 whitespace-nowrap ml-2"
          >
            {t("text_purchase_history")} &gt;
          </span>
        </div>
      </div>
    );
  } else if (type === 'normal' || isExpiredTrial) {
    // No active membership (normal user or expired trial)
    cardContent = (
      <div className="mx-auto mt-4 w-full" style={{ maxWidth: '300px' }}>
        <div className="bg-[#f8e4ea] border border-[#f8e4ea] rounded-t-lg p-3 flex justify-between items-center relative overflow-hidden gap-2">
          <div className="flex items-center gap-1.5 relative z-10 shrink min-w-0">
            <img
              alt="ESI-Logo"
              role="button"
              src={BASE_URL + "/logo.svg"}
              className="esi-logo h-5 max-w-[85px] object-contain shrink-0"
            />
            {isExpiredTrial && (
              <span className="bg-gray-200 text-gray-600 text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0">
                {t("text_expired", "已过期")}
              </span>
            )}
          </div>

          <div className="bg-gradient-to-r from-[#F4CC4A] to-[#FFFAC3] p-[1px] rounded-md shrink-0">
            <button
              onClick={handleUpgrade}
              className="bg-gradient-to-r from-[#FFFAC3] to-[#F4CC4A] text-[#7a5a00] text-[11px] font-bold px-2.5 py-1.5 rounded-md cursor-pointer hover:opacity-90 whitespace-nowrap shrink-0 block"
            >
              {t("text_activate_now")} &gt;
            </button>
          </div>
        </div>
        <div className="bg-[#fff0f4] rounded-b-lg px-3 py-2 flex justify-between items-center text-xs font-bold text-[#E50A47]">
          <span className="whitespace-nowrap">{isExpiredTrial && formattedDate ? `${formattedDate} ${t("text_valid_till")}` : '—'}</span>
          <span
            onClick={() => navigate('/profiles?tab=purchase-history')}
            className="cursor-pointer hover:opacity-80 shrink-0 whitespace-nowrap ml-2"
          >
            {t("text_purchase_history")} &gt;
          </span>
        </div>
      </div>
    );
  } else if (type === 'pro') {
    cardContent = (
      <div className="mx-auto mt-4 w-full shadow-sm rounded-xl overflow-hidden" style={{ maxWidth: '300px' }}>
        <div className="bg-[#f8e4ea] border border-[#f8e4ea] rounded-t-lg p-3 flex justify-between items-center text-[#9b2a46] relative gap-2">
          <div className="flex items-center gap-1.5 z-10 shrink min-w-0">
            <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo h-5 max-w-[70px] object-contain shrink-0' />
            <span className="inline-block bg-gradient-to-r from-[#E2005B] to-[#F8D5E1] p-[1px] rounded shrink-0">
              <span className="block bg-gradient-to-r from-[#FFE8EF] to-[#FA0366] text-black text-[9px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                {t("text_member")}
              </span>
            </span>
          </div>
          <div onClick={handleUpgrade} className="z-10 text-xs font-semibold flex items-center cursor-pointer text-[#E50A47] hover:opacity-80 shrink-0 whitespace-nowrap">
            {t("button_renew")} &gt;
          </div>
        </div>
        <div className="bg-[#fff0f4] rounded-b-lg px-3 py-2 flex justify-between items-center text-xs font-bold text-[#E50A47]">
          <span className="whitespace-nowrap">{formattedDate} {t("text_valid_till")}</span>
          <span onClick={() => navigate('/profiles?tab=purchase-history')} className="cursor-pointer hover:opacity-80 shrink-0 whitespace-nowrap ml-2">{t("text_purchase_history")} &gt;</span>
        </div>
      </div>
    );
  } else if (type === 'VIP') {
    cardContent = (
      <div className="mx-auto mt-4 w-full shadow-sm rounded-xl overflow-hidden" style={{ maxWidth: '300px' }}>
        <div className="bg-[#ec547f] rounded-t-lg p-3 flex justify-between items-center text-white relative gap-2">
          <div className="flex items-center gap-1.5 z-10 shrink min-w-0">
            <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo brightness-0 invert h-5 max-w-[70px] object-contain shrink-0' />
            <span className="bg-gradient-to-r from-[#ffe082] to-[#ffcc33] text-[#1A1B2D] text-[9px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap shrink-0">{t("text_svip_member")}</span>
          </div>
          <div onClick={handleUpgrade} className="z-10 text-xs font-semibold flex items-center cursor-pointer hover:opacity-80 text-white shrink-0 whitespace-nowrap">
            {t("button_renew")} &gt;
          </div>
        </div>
        <div className="bg-[#fff0f4] rounded-b-lg px-3 py-2 flex justify-between items-center text-xs font-bold text-[#E50A47]">
          <span className="whitespace-nowrap">{formattedDate} {t("text_valid_till")}</span>
          <span
            onClick={() => navigate('/profiles?tab=purchase-history')}
            className="cursor-pointer hover:opacity-80 shrink-0 whitespace-nowrap ml-2"
          >
            {t("text_purchase_history")} &gt;
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {cardContent}
      <PROModal
        open={isPROModalOpen}
        onCancel={() => {
          setIsPROModalOpen(false);
        }}
        onSuccess={onRefresh}
      />
    </>
  );
};

export default MemberCard;
